import { yupResolver } from "@hookform/resolvers/yup";
import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import Close from "@mui/icons-material/Close";
import { Box, type BoxProps, Button, Grid, IconButton, Stack, Typography } from "@mui/material";
import { countBy } from "lodash-es";
import { type FC, useCallback, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate, useSearchParams } from "react-router";
import { useFlag } from "../../server/optimizely";
import { ThemeProvider } from "../../styles/theme/theme-provider";
import { useDialog } from "../../utils/hooks/use-dialog";
import yup from "../../utils/yup";
import { usePendo } from "../analytics/hooks/use-pendo-watch";
import { useMultiformat } from "../multiformat/hooks/use-multi-format";
import { type CreateContentRequest, isLongform } from "../multiformat/store/types";
import { ContentDescriptionTextField } from "./content-description-textfield";
import { ContentToneSelect } from "./content-tone-select";
import { ContentTypeSelect } from "./content-type-select";
import { useBrandToneOptions } from "./hooks/use-brand-tone-options";
import { ImageStyleSelector } from "./image-style-selector";
import { InspireMeButton } from "./inspire-me-button";
import { LongformDialog } from "./longform-prompt-dialog";
import { type LongFormParams, mapLongformParams } from "./map-longform-params";
import { PhotoTypeSelect } from "./photo-type-select";

export const CreateContentCard: FC<{ onClose?: () => void } & BoxProps> = ({
  onClose,
  sx = [],
  ...props
}) => {
  const { t } = useTranslation();
  const multiformatLoader = useMultiformat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandToneOptions = useBrandToneOptions();
  const pendo = usePendo();
  const [photo_db] = useFlag("photo_db");

  const schema = useMemo(() => {
    return yup.object({
      q: yup.string().required(t("Please describe the content you wish to create.")),
      tone: yup
        .string()
        .oneOf(brandToneOptions.data?.map((option) => option.value) ?? [])
        .optional(),
      format: yup.string().optional(),
      image_style: yup.object({
        photo: yup.boolean().required(),
        illustration: yup.boolean().required(),
        icon: yup.boolean().required(),
        video: yup.boolean().required(),
      }),
      db: yup.string().optional(),
      file: yup
        .object({
          id: yup.string().optional(),
          name: yup.string().optional(),
        })
        .optional()
        .default(undefined),
    });
  }, [brandToneOptions, t]);

  const methods = useForm({
    defaultValues: {
      q: searchParams.get("q") ?? searchParams.get("answer") ?? "",
      format: searchParams.get("format") ?? "Communication",
      tone: searchParams.get("tone") ?? undefined,
      image_style: searchParams.get("image_styles")
        ? {
            photo: searchParams.get("image_styles")?.includes("photo") ?? true,
            illustration: searchParams.get("image_styles")?.includes("illustration") ?? true,
            icon: searchParams.get("image_styles")?.includes("icon") ?? true,
            video: searchParams.get("image_styles")?.includes("video") ?? true,
          }
        : {
            photo: true,
            illustration: true,
            icon: true,
            video: true,
          },
      db: searchParams.get("db") ?? undefined,
      file: searchParams.get("file_id")
        ? {
            id: searchParams.get("file_id") ?? undefined,
            name: searchParams.get("file_name") ?? undefined,
          }
        : undefined,
    },
    resolver: yupResolver(schema),
  });

  useWatch({ control: methods.control, name: ["format", "q"] });
  const [format, q] = [methods.getValues("format"), methods.getValues("q")];

  const longformDialog = useDialog<LongFormParams>();

  const handleSubmit = useCallback(
    (request: CreateContentRequest) => {
      if (!multiformatLoader.data || !format) return;

      const config = multiformatLoader.data.content_types.find((c) => c.name === format);
      const imageStyles =
        Object.entries(request?.image_style ?? {})
          .filter(([, value]) => value === true)
          .map(([key]) => key)
          .join(",") || null;

      const sharedParams = {
        format,
        ...(request.file && { file_id: request.file.id, file_name: request.file.name }),
        ...(imageStyles && { image_styles: imageStyles }),
        ...(request.db && { db: request.db }),
        ...(request.tone && { tone: request.tone }),
      };

      if (config?.type === "Communication") {
        pendo?.track("create-content-prompt", {
          prompt: request.q,
          ...sharedParams,
        });
        const pathname = "/generate-comms";
        const params = createSearchParams({
          q: request.q,
          ...sharedParams,
        });
        void navigate({ pathname, search: params.toString() });
        return;
      }

      if (isLongform(format) && request.q.length < 500) {
        pendo?.track("create-content-prompt-too-short", {
          prompt: request.q,
          ...sharedParams,
        });
        longformDialog.handleOpen(sharedParams);
        return;
      }

      pendo?.track("create-content-prompt", {
        prompt: request.q,
        ...sharedParams,
      });
      const params = createSearchParams({
        answer: request.q,
        ...sharedParams,
      });
      void navigate({ pathname: "/create-content/preview", search: params.toString() });

      onClose?.();
    },
    [multiformatLoader.data, format, pendo, navigate, onClose, longformDialog],
  );

  const handleInspire = useCallback(
    (prompt: string) => {
      methods.setValue("q", prompt);
    },
    [methods],
  );

  const handleSkip = useCallback(
    (longFormParams?: LongFormParams) => {
      const params = createSearchParams({
        answer: q,
        format: format ?? "",
        ...mapLongformParams(longFormParams ?? {}),
      });
      void navigate({ pathname: "/create-content/preview", search: params.toString() });
      longformDialog.handleClose();
    },
    [q, format, navigate, longformDialog],
  );

  const customToneEnabled = (
    multiformatType: string | undefined,
    queryFormat: string | undefined,
  ): boolean => {
    const noCustomToneTypes = ["Landscape Logo", "Square Logo"];
    return (
      !noCustomToneTypes.includes(multiformatType ?? "") &&
      !noCustomToneTypes.includes(queryFormat ?? "")
    );
  };

  const handleContinue = useCallback(
    (prompt: string, longFormParams?: LongFormParams) => {
      const params = createSearchParams({
        answer: prompt,
        format: format ?? "",
        ...mapLongformParams(longFormParams ?? {}),
      });
      void navigate({ pathname: "/create-content/preview", search: params.toString() });
      longformDialog.handleClose();
    },
    [format, navigate, longformDialog],
  );

  const imageStyleConfig = useMemo(() => {
    if (multiformatLoader.loading) return;
    return countBy(
      multiformatLoader.data?.content_types
        .find((c) => c.name === format)
        ?.layouts?.map((l) => l.images[0]?.type),
    );
  }, [multiformatLoader.data, multiformatLoader.loading, format]);

  return (
    <FormProvider {...methods}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Box
          sx={[
            (theme) => ({
              backgroundColor: "neutral.900",
              backgroundPosition: "right bottom, left top, right 40px top 40px",
              backgroundRepeat: "no-repeat",
              backgroundImage:
                'url("https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/b755211d-9387-4973-832c-3aa1e3c3a414"), \
                url("https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/037dff25-8e4d-40e0-be06-641ff8a4569c"), \
                url("https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/a5807452-5f9f-4b4e-9b19-f37e0d4d8c62")',
              p: 5,

              [theme.breakpoints.down("md")]: {
                pr: 3,
                pb: 3,
                backgroundImage:
                  'url("https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/b755211d-9387-4973-832c-3aa1e3c3a414"), \
                url("https://images.changeengine.com/2286f56c-57aa-4313-bc88-fde38caaa844/037dff25-8e4d-40e0-be06-641ff8a4569c")',
              },
            }),
            ...(sx instanceof Array ? sx : [sx]),
          ]}
          {...props}
        >
          <ThemeProvider mode="dark">
            {onClose && (
              <IconButton
                data-analytics-id="create-content-close"
                onClick={onClose}
                sx={(theme) => ({
                  position: "relative",
                  float: "right",
                  top: theme.spacing(-3),
                  right: theme.spacing(-1),
                  [theme.breakpoints.down("md")]: {
                    right: theme.spacing(2),
                  },
                })}
              >
                <Close data-testid="CloseIcon" />
              </IconButton>
            )}
            <Typography variant="h5" color="text.primary">
              {t("Create Content Instantly with AI!")}
            </Typography>
            <Typography variant="body2" mt={1} color="text.primary">
              {t("Let our powerful AI generate complete content for you in seconds.")}
            </Typography>
            <Grid container spacing={4} my={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack gap={2}>
                  <Typography variant="caption" mt={1} color="text.primary" mb={1}>
                    {t("Style")}
                  </Typography>
                  <ContentTypeSelect />
                  {multiformatLoader.data &&
                    customToneEnabled(
                      multiformatLoader.data.content_types.find((c) => c.name === format)?.type,
                      format,
                    ) && <ContentToneSelect />}
                  {imageStyleConfig && <ImageStyleSelector config={imageStyleConfig} />}
                  {photo_db && <PhotoTypeSelect />}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack gap={2}>
                  <Stack gap={1} direction="row">
                    <Typography variant="caption" mt={1} color="text.primary">
                      {t("Description")}
                    </Typography>
                    <InspireMeButton onInspire={handleInspire} format={format ?? ""} />
                  </Stack>
                  <ContentDescriptionTextField
                    fileUpload={Boolean(
                      format &&
                        ![
                          "Square Logo",
                          "Landscape Logo",
                          "TV Display",
                          "Video Call Background",
                          "Certificate",
                          "Horizontal Banner",
                          "Meme",
                          "Social Post",
                        ].includes(format),
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          </ThemeProvider>
          <Stack direction="row" justifyContent="center">
            <Button
              data-analytics-id="create-content-generate"
              type="submit"
              variant="contained"
              size="large"
              sx={(theme) => ({
                mt: 2,
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                },
              })}
              startIcon={<AutoFixHigh />}
            >
              {t("Generate Content")}
            </Button>
          </Stack>
        </Box>
        {longformDialog.open && (
          <LongformDialog
            open={longformDialog.open}
            contentType={format ?? ""}
            prompt={q}
            onSkip={() => handleSkip(longformDialog.data)}
            onContinue={(prompt) => handleContinue(prompt, longformDialog.data)}
            onClose={longformDialog.handleClose}
          />
        )}
      </form>
    </FormProvider>
  );
};
